import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const BurgerContainer = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs};
  position: absolute;
  right: ${({ theme }) => theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  z-index: 1002;
  touch-action: manipulation;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 40px;
    height: 40px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    right: ${({ theme }) => theme.spacing.sm};
    width: 35px;
    height: 35px;
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
  }
`;

const BurgerLine = styled(motion.span)<{ isOpen: boolean }>`
  display: block;
  width: 25px;
  height: 3px;
  background-color: ${({ theme, isOpen }) => isOpen ? theme.colors.primary : theme.colors.background};
  border-radius: 2px;
  transform-origin: center;
  transition: background-color 0.3s ease;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 22px;
    height: 2.5px;
  }
`;

interface BurgerMenuProps {
  isOpen: boolean;
  onClick: () => void;
}

const BurgerMenu: React.FC<BurgerMenuProps> = ({ isOpen, onClick }) => {
  const topLineVariants = {
    closed: { 
      rotate: 0, 
      y: 0,
      transition: { duration: 0.3, ease: "easeInOut" as const }
    },
    open: { 
      rotate: 45, 
      y: 8,
      transition: { duration: 0.3, ease: "easeInOut" as const }
    }
  };

  const middleLineVariants = {
    closed: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.3, ease: "easeInOut" as const }
    },
    open: { 
      opacity: 0, 
      x: -10,
      transition: { duration: 0.3, ease: "easeInOut" as const }
    }
  };

  const bottomLineVariants = {
    closed: { 
      rotate: 0, 
      y: 0,
      transition: { duration: 0.3, ease: "easeInOut" as const }
    },
    open: { 
      rotate: -45, 
      y: -8,
      transition: { duration: 0.3, ease: "easeInOut" as const }
    }
  };

  return (
    <BurgerContainer 
      onClick={onClick} 
      aria-label={isOpen ? "Close menu" : "Open menu"}
      aria-expanded={isOpen}
    >
      <BurgerLine
        isOpen={isOpen}
        variants={topLineVariants}
        animate={isOpen ? 'open' : 'closed'}
        style={{ marginBottom: '5px' }}
      />
      <BurgerLine
        isOpen={isOpen}
        variants={middleLineVariants}
        animate={isOpen ? 'open' : 'closed'}
        style={{ marginBottom: '5px' }}
      />
      <BurgerLine
        isOpen={isOpen}
        variants={bottomLineVariants}
        animate={isOpen ? 'open' : 'closed'}
      />
    </BurgerContainer>
  );
};

export default BurgerMenu;